import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from '../../shared/axios/axios';
import originAxios from 'axios';
import { Spin, Collapse, Tooltip, Button, Space } from 'antd';
import { PdfEditorComponent } from '../../shared/components/pdf-editor';
import { isGuid, stringToArrayBuffer } from '../../shared/utils';
import { errorText } from '../../shared/constants/errorText';
import SignatureLookup from './pdf-prefill-data-sources-selector-form/signature-lookup';

import * as actions from './store/actions';
import * as signatureActions from '../signature/store/actions';

import './styles.less';

const { Panel } = Collapse;

const AdditionalFiles = ({
  rootContainer,
  formId,
  policyId,
  insuredId,
  registerFileSaveCallback,
  onDeleteAppendFile,
  dataSourceValues,
  updateFormParameters,
  formParameters,
  registerFormClearCallback,
  refreshFile,
  generateFile,
}) => {
  const dispatch = useDispatch();

  const [initialFormValuesLoaded, setInitialFormValuesLoaded] = useState(false);
  const [prefilledFormDataServer, setPrefilledFormDataServer] = useState();
  const [prefilledFormDataLocal, setPrefilledFormDataLocal] = useState();
  const [clearFormCallback, setClearFormCallback] = useState();
  const [showSignatureOnPdf, setShowSignatureOnPdf] = useState();
  const [signatureSaveCallback, setSignatureSaveCallback] = useState();
  const [signatureBytes, setSignatureBytes] = useState();
  const [localFormName, setLocalFormName] = useState();
  const [getFormParametersResult, setGetFormParametersResult] = useState();
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [initiatingDone, setInitiatingDone] = useState(false);

  const signatureBytesLoading = useSelector((state) => state.signature.signatureBytesLoading);
  const prefilledFormLoading = useSelector((state) => state.formEditor.prefilledFormLoading);
  const permissions = useSelector((state) => state.permissions.permissions);

  useEffect(() => {
    setInitiatingDone(true);
    generateSource();
  }, []);

  useEffect(() => {
    if (initiatingDone) {
      generateSource();
    }
  }, [generateFile]);

  useEffect(() => {
    if (initiatingDone) {
      if (initialFormValuesLoaded) {
        refreshSource();
      } else {
        generateSource();
      }
    }
  }, [refreshFile]);

  useEffect(() => {
    if (typeof registerFormClearCallback === 'function') {
      registerFormClearCallback(clearFormFields);
    }
  }, [registerFormClearCallback, clearFormFields, clearFormCallback]);

  const generateSource = () => {
    setPrefilledFormDataLocal(undefined);
    setInitialFormValuesLoaded(false);
    dispatch(actions.getFormParameters({ formId, policyId })).then((result) => {
      if (result) {
        const newFormParameters = { ...formParameters };
        for (const [key, value] of Object.entries(result.formLookupData)) {
          newFormParameters.formLookupData[key] = formParameters.formLookupData[key] || value;
        }

        updateFormParameters(newFormParameters);

        const data = {
          policyIds: isGuid(policyId) ? [policyId] : undefined,
          ...dataSourceValues,
          formName: result.formName,
        };

        setLocalFormName(result.formName);
        setGetFormParametersResult(result);
        getFormBytes(data, result);
        setInitialFormValuesLoaded(true);
      }
    });
  };

  const refreshSource = () => {
    const data = {
      policyIds: isGuid(policyId) ? [policyId] : undefined,
      ...dataSourceValues,
      formName: localFormName,
    };

    getFormBytes(data, getFormParametersResult, prefilledFormDataLocal);
  };

  const getFormBytes = (formDataSource, formParameters, prefilledFormData) => {
    const data = {
      formDataSource: { ...formDataSource, isReset: undefined },
      previousVersionFormDataSource: prefilledFormData?.previousVersionFormDataSource,
      formId: formId,
      truckingCompanyId: insuredId,
      policyId: policyId,
      defaultFilled: prefilledFormData?.defaultFilled,
      formFieldNames: prefilledFormData?.formFieldNames,
      currentFileData: prefilledFormData?.currentFileData,
      parentFileData: prefilledFormData?.parentFileData,
      isReset: formDataSource.isReset,
    };

    getPrefilledForm({
      data,
      isUserForm: formParameters.isUserForm,
      originalFileId: formParameters.originalFileId,
      isCustomForm: formParameters.isCustomForm,
      isAdditionalFile: true,
    }).then((x) => {
      setPrefilledFormDataServer({ ...x });
      setPrefilledFormDataLocal({ ...x });
    });
  };

  let getPrefilledFormCancel;
  const getPrefilledForm = ({ data, isUserForm, originalFileId, isCustomForm, isAdditionalFile }) => {
    return new Promise((resolve, reject) => {
      let url = `/form-editor/get-prefilled-form`;

      if (typeof getPrefilledFormCancel == 'function') {
        getPrefilledFormCancel(errorText.canceledByFetchingNewData);
      }

      if (data?.formId) {
        url += `?fileId=${data.formId}`;

        url += `&truckingCompanyId=${data.truckingCompanyId}`;

        if (isGuid(data.quoteRequestId)) {
          url += `&quoteRequestId=${data.quoteRequestId}`;
        }

        if (isGuid(data.policyId)) {
          url += `&policyId=${data.policyId}`;
        }

        if (isCustomForm) {
          url += `&isCustomForm=${isCustomForm}`;
        }

        if (isUserForm) {
          url += `&isUserForm=${isUserForm}`;
          url += `&originalFileId=${originalFileId}`;
        }

        if (isAdditionalFile) {
          url += `&isAdditionalFile=${isAdditionalFile}`;
        }
      }

      axios
        .post(url, data, {
          cancelToken: new originAxios.CancelToken((cancel) => {
            getPrefilledFormCancel = cancel;
          }),
        })
        .then((response) => {
          if (response.data?.fileBytes) {
            response.data.fileBytes = stringToArrayBuffer(response.data?.fileBytes);
          }

          resolve(response.data);
        })
        .catch((err) => {
          if (originAxios.isCancel(err) && err.message === errorText.canceledByFetchingNewData) {
            resolve(err);
          } else {
            reject(err);
          }
        });
    });
  };

  const clearFormFields = useCallback(() => {
    if (prefilledFormDataLocal) {
      prefilledFormDataLocal.previousVersionFormDataSource = { defaultFilled: prefilledFormDataLocal.previousVersionFormDataSource.defaultFilled };
    }

    setPrefilledFormDataLocal(prefilledFormDataLocal);

    clearFormCallback();
  }, [prefilledFormDataLocal, setPrefilledFormDataLocal, clearFormCallback]);

  const onSaveSignatureClick = () => {
    signatureSaveCallback();
    setShowSignatureOnPdf(false);
  };

  const setSignatureId = (value) => {
    dispatch(signatureActions.getSignatureBytes(value)).then((x) => {
      setSignatureBytes(x);
      setShowSignatureOnPdf(true);
    });
  };

  const onCancelSignatureClick = () => {
    setShowSignatureOnPdf(false);
    setSignatureBytes(undefined);
  };

  const onSignatureChange = (value, options) => {
    setSignatureId(value.key);
  };

  const onCreateSignatureClick = () => {
    setShowSignatureModal(true);
  };

  const onCloseCreateSignatureModal = () => {
    setShowSignatureModal(false);
  };

  return !initialFormValuesLoaded ? (
    <Spin spinning={true} />
  ) : (
    <Collapse defaultActiveKey="1" className="append-collapse">
      <Panel
        collapsible="icon"
        showArrow={false}
        header={
          <div>
            <Space>
              {localFormName ?? 'Append file'}

              <SignatureLookup
                rootContainer={rootContainer}
                onSignatureChange={onSignatureChange}
                showSignatureOnPdf={showSignatureOnPdf}
                formParameters={formParameters}
                onSaveSignatureClick={onSaveSignatureClick}
                onCancelSignatureClick={onCancelSignatureClick}
                permissions={permissions}
                onCreateSignatureClick={onCreateSignatureClick}
                showSignatureModal={showSignatureModal}
                onCloseCreateSignatureModal={onCloseCreateSignatureModal}
                insuredId={insuredId}
              />
            </Space>

            <Tooltip placement="bottomRight" title="Delete append file" getPopupContainer={rootContainer}>
              <Button type="primary" danger style={{ float: 'right' }} onClick={onDeleteAppendFile}>
                Delete file
              </Button>
            </Tooltip>
          </div>
        }
        key="1"
        className="pdf-prefill-filter-panel"
        forceRender
      >
        <div className="form-editor">
          {!prefilledFormDataServer ? (
            <Spin spinning={true} />
          ) : (
            <PdfEditorComponent
              fileBytes={prefilledFormDataServer?.fileBytes}
              clearFieldNames={prefilledFormDataServer?.previousVersionFormDataSource?.mappedFieldName?.clearFieldNames}
              fieldsToFill={prefilledFormDataServer?.previousVersionFormDataSource?.mappedFieldName?.fieldsToFill}
              registerFormSaveCallback={registerFileSaveCallback}
              registerFormClearCallback={(callback) => setClearFormCallback(() => callback)}
              registerSignatureSaveCallback={(callback) => setSignatureSaveCallback(() => callback)}
              rootContainer={rootContainer}
              loading={prefilledFormLoading || signatureBytesLoading}
              showSignatureOnPdf={showSignatureOnPdf}
              signatureBytes={signatureBytes}
            />
          )}
        </div>
      </Panel>
    </Collapse>
  );
};

export default AdditionalFiles;
